import React, { useContext, useEffect, useMemo, useState } from "react";
import TableChart, { type TableChartRowData } from "../TableChart";
import { useQuery } from "@apollo/client";
import { GET_SLA_RULES } from "../../../../Rules/SLACustomization/SLACustomizationApi";
import useSummary from "../../../useSummary";
import { statusCategories } from "../../../../Findings/FindingDetailsPane/FindingDetailsPaneHeader/Statuses/StatusUtils";
import useLogger from "../../../../Logger/useLogger";
import {
  createColumnHelper,
  type CellContext,
  type ColumnDef,
} from "@tanstack/react-table";
import "./AvgFixTimeBySLARules.scss";
import {
  AppPreferencesContext,
  userActionsEnum,
} from "../../../../General/AppPreferencesProvider";
import useSetDataWithExport from "../useSetDataWithExport";
import useDebounceLoading from "../useDebounceLoading";
import {
  Tooltip,
  TextButton,
  TableCell,
  SelectInput,
  type SelectOption,
} from "@SeemplicityDev/shared";
import type { SelectedScope } from "../../../../types/scope";

export interface AvgFixTimeBySLARulesTableProps {
  selectedScope?: SelectedScope | null;
  scopeToExecute: string | null;
  filterToExecute: string | null;
  isForExport?: boolean;
  setTotalWidgets?: ((total: number) => void) | null;
  index?: number | null;
  setIsReadyToExport?: ((ready: boolean) => void) | null;
}

// SLA rule node as returned by GET_SLA_RULES (sla_preferences.edges[].node).
interface SlaRule {
  id: string;
  name: string;
  time_to_respond: number;
}

// Row rendered by the table: an SLA rule augmented with computed fix-time data.
interface AvgFixTimeRow extends SlaRule {
  avgFixTime: number;
  ruleName: number;
}

// Raw summary rows arrive as [ruleId, avgFixTime] tuples from the GraphQL
// summary query (typed as unknown at the useSummary boundary).
type SummaryRow = [number, number];

const AvgFixTimeBySLARulesTable: React.FC<AvgFixTimeBySLARulesTableProps> = ({
  selectedScope,
  scopeToExecute,
  filterToExecute,
  isForExport = false,
  setTotalWidgets = null,
  index = null,
  setIsReadyToExport = null,
}) => {
  const [slaFixTimeData, setSlaFixTimeData] = useState<AvgFixTimeRow[]>([]);
  const [slaRules, setSlaRules] = useState<SlaRule[]>([]);
  const [selectedRules, setSelectedRules] = useState<SelectOption[]>([]);
  const { logError } = useLogger();
  const { isAllowed } = useContext(AppPreferencesContext);

  const { summaryData, summaryDataError, summaryLoading } = useSummary({
    variables: {
      name: "FINDINGS_VIEW",
      selection: ["finding.age"],
      group_by: ["finding.sla_preference_id"],
      aggregate_functions: ["AVG"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute || null,
        scopesjson: scopeToExecute || null,
        scopesid: selectedScope?.value?.id,
        filters_fields_to_override: [
          JSON?.stringify({
            field: "actual_status",
            condition: "",
            value: {
              field: "category",
              condition: "in",
              value: [statusCategories.IGNORED, statusCategories.FIXED],
            },
          }),
        ],
      },
    },
    skip:
      (!scopeToExecute && !selectedScope?.value?.id) ||
      !slaRules?.length ||
      !filterToExecute,
  });

  const {
    data: slaRulesData,
    loading: slaRulesLoading,
    error: slaRulesError,
  } = useQuery(GET_SLA_RULES);

  const { setDataWithExport, setLoadedRowsCount } =
    useSetDataWithExport<AvgFixTimeRow>({
      isForExport,
      index,
      setTotalWidgets,
      setData: setSlaFixTimeData,
      // Behavior preserved from JS: readiness is gauged against the SLA rules
      // list (loadedRowsCount === data?.length), not the fix-time rows. Cast at
      // this boundary since the hook is generic over the setData row type.
      data: slaRules as unknown as AvgFixTimeRow[],
      error: slaRulesError || summaryDataError,
      loading: slaRulesLoading || summaryLoading,
      setIsReadyToExport,
    });

  useEffect(() => {
    if (!!slaRulesData) {
      setSlaRules(
        slaRulesData?.sla_preferences?.edges?.map(
          (preference: { node: SlaRule }) => preference.node
        )
      );
      setLoadedRowsCount(slaRulesData?.sla_preferences?.edges?.length);
    }
  }, [slaRulesData]);

  useEffect(() => {
    if (slaRules?.length && !!summaryData) {
      const dataArray = (summaryData as SummaryRow[]).reduce<AvgFixTimeRow[]>(
        (acc, el) => {
          const newEl = [...slaRules]?.find(
            (dataEl) => parseInt(atob(dataEl?.id).split(":")?.[1]) === el[0]
          );

          if (!newEl) {
            logError(
              new Error(
                `sla rules do not contain rule with id: ${el[0]}.\nChart: AvgFixTimeByRule.`
              ),
              `sla rules does not contain rule with id: ${el[0]}.\nChart: AvgFixTimeByRule.`
            );
          } else {
            acc.push({ ...newEl, avgFixTime: el[1], ruleName: el[0] });
          }

          return acc;
        },
        []
      );

      dataArray?.sort((a, b) => a.time_to_respond - b.time_to_respond);
      setSelectedRules(
        dataArray?.map((el) => ({
          value: el.id,
          label: el.name,
        }))
      );

      setDataWithExport(dataArray);
    }
  }, [slaRules, summaryData]);

  const displayedData = useMemo(() => {
    if (!selectedRules?.length || !slaFixTimeData?.length) {
      return [];
    }

    return slaFixTimeData?.filter(
      (rule) => !!selectedRules?.find((selected) => selected.value === rule.id)
    );
  }, [selectedRules, slaFixTimeData]);

  const columnHelper = createColumnHelper<AvgFixTimeRow>();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        cell: (props: CellContext<AvgFixTimeRow, string>) => (
          <TableCell>
            <Tooltip content={props.getValue()}>
              <span>{props.getValue()}</span>
            </Tooltip>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Rule Name",
        minSize: 50,
      }),
      columnHelper.accessor("time_to_respond", {
        cell: (props: CellContext<AvgFixTimeRow, number>) => (
          <TableCell>
            <Tooltip content={props.getValue()}>
              <span>{props.getValue()} days</span>
            </Tooltip>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Expected SLA",
        minSize: 50,
      }),
      columnHelper.accessor("avgFixTime", {
        cell: (props: CellContext<AvgFixTimeRow, number>) => (
          <TableCell>
            <div className={"avg-fix-wrapper"}>
              <Tooltip
                type="info"
                content={
                  props.getValue() > props.row?.original?.time_to_respond
                    ? "Overdue"
                    : "On-Time"
                }
              >
                <i
                  className={
                    props.getValue() > props.row?.original?.time_to_respond
                      ? "seem-icon-in-time overdue"
                      : "seem-icon-in-time on-time"
                  }
                />
              </Tooltip>
              <span className={"avg-fix-title"}>{props.getValue()}</span>
            </div>
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Avg Fix Time (days)",
        minSize: 50,
      }),
    ];
  }, [displayedData]);

  const selectorHeader = useMemo(() => {
    return (
      <div className={"select-header-wrapper"}>
        <SelectInput
          data-testid={`fix-time-rule-selection`}
          wrapperClassName={"table-widget-input-selector"}
          isMulti
          noMargin
          size={"custom"}
          name={"rules-selector"}
          setDefaultSingleOption={false}
          value={selectedRules}
          options={slaFixTimeData?.map((el) => ({
            value: el.id,
            label: el.name,
          }))}
          onChange={(selectedRules: SelectOption | SelectOption[]) => {
            setSelectedRules(
              !Array.isArray(selectedRules) ? [selectedRules] : selectedRules
            );
          }}
          placeholder={"Select rules"}
        />
        {isAllowed(userActionsEnum.EDIT_SLA_SETTINGS) && (
          <TextButton
            buttonStyle={"textLink"}
            label={"SLA Rules"}
            onClick={() =>
              window.open("/policies/findings-rules/sla", "_blank")
            }
          />
        )}
      </div>
    );
  }, [slaFixTimeData, selectedRules]);

  const isLoading = useDebounceLoading([summaryLoading, slaRulesLoading]);

  return (
    <TableChart
      columns={columns as unknown as ColumnDef<TableChartRowData, unknown>[]}
      tableData={displayedData as unknown as TableChartRowData[]}
      isLoading={isLoading}
      error={slaRulesError || summaryDataError}
      selectorHeader={selectorHeader}
      noSelectedData={selectedRules?.length === 0 && !!slaFixTimeData.length}
      noSelectedLoaderTitle={"No SLA rule selected"}
    />
  );
};

export default AvgFixTimeBySLARulesTable;
